import { Link } from "react-router-dom";
import { TopBar } from "../../components/TopBar";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { useProjects } from "../../context/ProjectContext";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../api/client";
import { timeAgo } from "../../lib/format";
import { windowLabel } from "../../lib/format";
import type { Leaderboard } from "../../api/types";

/** Operator-meaningful tags for a board (not storage internals like the score type). */
function boardTags(sort: string, reset: unknown) {
  const tags = [{ label: sort === "asc" ? "Lowest wins" : "Highest wins", cls: "tag-dim" }];
  if (reset) tags.push({ label: "Seasonal", cls: "tag-violet" });
  return tags;
}

/** The group prefix of a board id (`arcade:daily` -> `arcade`), or null if ungrouped. */
function groupPrefix(id: string): string | null {
  const i = id.indexOf(":");
  return i < 0 ? null : id.slice(0, i);
}

export function Boards() {
  const { active } = useProjects();
  const pid = active?.id ?? "";
  const { data, loading, error } = useAsync(() => api.listBoards(pid), [pid]);

  // Phase 11.4: a board with `windows` is a GROUP; its children are `<id>:<window>`. Fold the
  // children into their parent so the grid shows one card per logical leaderboard, with the
  // child windows as variants.
  const boards = data ?? [];
  const groupIds = new Set(boards.filter((b) => b.windows && b.windows.length).map((b) => b.id));
  const childrenSize = new Map<string, number>();
  for (const b of boards) {
    const pfx = groupPrefix(b.id);
    if (pfx && groupIds.has(pfx)) {
      childrenSize.set(pfx, (childrenSize.get(pfx) ?? 0) + b.size);
    }
  }
  // Top-level cards: everything that is NOT a child of a group.
  const topLevel = boards.filter((b) => {
    const pfx = groupPrefix(b.id);
    return !(pfx && groupIds.has(pfx));
  });

  const card = (b: Leaderboard) => {
    const isGroup = !!(b.windows && b.windows.length);
    const size = isGroup ? (childrenSize.get(b.id) ?? 0) : b.size;
    return (
      <Link key={b.id} to={`/app/boards/${encodeURIComponent(b.id)}`} className="card board-card">
        <div className="between">
          <span className="bc-name">{b.name || b.id}</span>
          {isGroup ? (
            <span className="tag tag-violet">{b.windows!.length} windows</span>
          ) : (
            <span className="tag tag-dim">{b.visibility === "public" ? "Public" : "Private"}</span>
          )}
        </div>
        <div className="bc-id mono faint">{b.id}</div>
        <div className="bc-tags">
          {isGroup
            ? b.windows!.map((w) => <span key={w} className="tag tag-mint">{windowLabel(w)}</span>)
            : boardTags(b.sort_order, b.reset).map((t, i) => (
                <span key={i} className={`tag ${t.cls}`}>{t.label}</span>
              ))}
        </div>
        <div className="bc-foot">
          <div>
            <div className="bc-size">{size.toLocaleString()}<small> entries{isGroup ? " · all windows" : ""}</small></div>
            {!isGroup && b.recent_updates != null && (
              <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>{b.recent_updates} in 24h</div>
            )}
          </div>
          <div className="faint" style={{ fontSize: 12, textAlign: "right" }}>
            {timeAgo(b.last_activity_at ?? b.updated_at)}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <>
      <TopBar
        crumbs={<b>Leaderboards</b>}
        actions={<Link to="/app/boards/new" className="btn btn-primary btn-sm">＋ New leaderboard</Link>}
      />
      <div className="content">
        <PageHeader
          title="Leaderboards"
          intent={<>All your boards for <b>{active?.name}</b> — click one to inspect live rankings.</>}
        />

        {error && <div className="auth-err" style={{ marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <div className="board-grid">
            {[0, 1, 2].map((i) => <div key={i} className="card board-card"><div className="skel" style={{ height: 80 }} /></div>)}
          </div>
        ) : topLevel.length === 0 ? (
          <EmptyState
            icon="🪜"
            title="No leaderboards yet"
            action={<Link to="/app/boards/new" className="btn btn-primary">Create your first leaderboard</Link>}
          >
            Pick how scores rank, whether the board resets each season, and who can see it — everything
            has a sensible default, so you can start with just a name.
          </EmptyState>
        ) : (
          <div className="board-grid">
            {topLevel.map(card)}
          </div>
        )}
      </div>
    </>
  );
}
